import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Building2, Save, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

export function CompanyProfileSection() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    company_description: "",
    company_website: "",
    email_signature: "",
    company_tagline: "",
  });

  useEffect(() => {
    if (user) {
      loadCompanyProfile();
    }
  }, [user]);

  const loadCompanyProfile = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get user's org_id from profile
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user.id)
        .single();

      if (!userProfile?.org_id) {
        console.log("No organization found for user");
        setLoading(false);
        return;
      }

      // Get organization data
      const { data: org, error } = await supabase
        .from('organizations')
        .select('company_description, company_website, email_signature, company_tagline')
        .eq('id', userProfile.org_id)
        .single();

      if (error) throw error;

      if (org) {
        setProfile({
          company_description: org.company_description || "",
          company_website: org.company_website || "",
          email_signature: org.email_signature || "",
          company_tagline: org.company_tagline || "",
        });
      }
    } catch (error: any) {
      console.error('Error loading company profile:', error);
      toast.error("Failed to load company profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Get user's org_id from profile
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('id', user?.id)
        .single();

      if (!userProfile?.org_id) {
        toast.error("No organization found");
        return;
      }

      const { error } = await supabase
        .from('organizations')
        .update({
          company_description: profile.company_description,
          company_website: profile.company_website,
          email_signature: profile.email_signature,
          company_tagline: profile.company_tagline,
        })
        .eq('id', userProfile.org_id);

      if (error) throw error;

      toast.success("Company profile updated successfully");
    } catch (error: any) {
      console.error('Error saving company profile:', error);
      toast.error("Failed to save company profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Company Profile
        </CardTitle>
        <CardDescription>
          Configure your company information for AI-generated emails and communications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="company_website">Company Website</Label>
          <Input
            id="company_website"
            placeholder="https://yourcompany.com"
            value={profile.company_website}
            onChange={(e) => setProfile({ ...profile, company_website: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Your company's website URL
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="company_tagline">Company Tagline</Label>
          <Input
            id="company_tagline"
            placeholder="Building Excellence, One Roof at a Time"
            value={profile.company_tagline}
            onChange={(e) => setProfile({ ...profile, company_tagline: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            A short, memorable description of your company
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="company_description">Company Description</Label>
          <Textarea
            id="company_description"
            rows={4}
            placeholder="Describe your company, services, values, and what makes you unique..."
            value={profile.company_description}
            onChange={(e) => setProfile({ ...profile, company_description: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            AI will use this to personalize emails and understand your business context
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email_signature">Email Signature</Label>
          <Textarea
            id="email_signature"
            rows={6}
            placeholder={`Best regards,\nJohn Smith\nOwner, Your Company Name\n(555) 123-4567\nwww.yourcompany.com`}
            value={profile.email_signature}
            onChange={(e) => setProfile({ ...profile, email_signature: e.target.value })}
          />
          <p className="text-xs text-muted-foreground">
            Default signature to include in AI-generated emails
          </p>
        </div>

        <Button onClick={handleSave} disabled={saving} className="w-full">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Company Profile
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
